import * as React from 'react';

import { DetailsTableCell } from '@amzn/imdb-shared-meridian-components/components/DetailsTable/DetailsTableCell';
import { CustomSingleCellDetailsTableRow } from '@amzn/imdb-shared-meridian-components/components/DetailsTable/DetailsTableRow';
import { NewWindowLink } from '@amzn/imdb-shared-meridian-components/components/link';
import Box from '@amzn/meridian/box';
import Table from '@amzn/meridian/table';
import Tooltip from '@amzn/meridian/tooltip';

import { ListDetails, ListFlag } from '../../../../../listoramaAdmin-api/generated-src/api';
import { getListWebsiteUrl } from '../../../../../utils/url';

type ListMainDetailsCellId =
    | 'Name'
    | 'Description'
    | 'State'
    | 'Visibility'
    | 'URL'
    | 'Flags'
    | 'Created'
    | 'Last Modified';

export interface ListMainDetailsTableProps {
    list: ListDetails;
}

export const ListMainDetailsTable: React.FC<ListMainDetailsTableProps> = (props: ListMainDetailsTableProps) => {
    const { list } = props;
    const listMainDetailsTableRows = createTableRows(list);

    return (
        <Table
            data-test-id='listMainDetailsTable'
            showDividers={true}
            spacing='small'
            rowComponents={[CustomSingleCellDetailsTableRow]}
        >
            {listMainDetailsTableRows.map((row) => (
                <CustomSingleCellDetailsTableRow
                    key={row.name}
                    header={row.name}
                    data={row.data}
                    getTableCell={getListMainDetailsTableCell}
                />
            ))}
        </Table>
    );
};

interface CustomTableRowProps<T extends string> {
    name: T;
    data: string;
}

const getListMainDetailsTableCell = (cellId: ListMainDetailsCellId, data: string) => {
    switch (cellId) {
        case 'URL': {
            return (
                <DetailsTableCell
                    data-test-id='listLink'
                    CustomDataElement={<NewWindowLink url={data} text={data} />}
                />
            );
        }
        case 'Visibility': {
            const [visibility, suppressed] = data.split('|');
            return (
                <DetailsTableCell
                    CustomDataElement={
                        <span style={{ display: 'flex', gap: '6px' }}>
                            {visibility}
                            {suppressed === 'true' && (
                                <Tooltip
                                    position='end'
                                    title={
                                        'Lists marked with a spam flag are treated as "private-by-default", ' +
                                        'and are not displayed to users other than the owner.'
                                    }
                                >
                                    <Box type='outline' backgroundColor='Maroon'>
                                        SUPPRESSED
                                    </Box>
                                </Tooltip>
                            )}
                        </span>
                    }
                />
            );
        }
        default:
            return <DetailsTableCell data={data} />;
    }
};

const createTableRows = (list: ListDetails): CustomTableRowProps<ListMainDetailsCellId>[] => [
    {
        name: 'Name',
        data: list.name
    },
    {
        name: 'Description',
        data: list.originalDescription ? list.originalDescription : 'No description'
    },
    {
        name: 'State',
        data: list.state
    },
    {
        name: 'Visibility',
        data: `${list.publik ? 'Public' : 'Private'}|${list.flags.includes(ListFlag.Spam)}`
    },
    {
        name: 'URL',
        data: getListWebsiteUrl(list.listId)
    },
    {
        name: 'Flags',
        data: list.flags.length > 0 ? list.flags.join(', ') : 'No flags'
    },
    {
        name: 'Created',
        data: list.created
    },
    {
        name: 'Last Modified',
        data: list.modified
    }
];
